import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage, auth } from './firebase'; 
import { signOut, updatePassword, onAuthStateChanged } from "firebase/auth";
import { 
  collection, addDoc, getDocs, deleteDoc, updateDoc, doc, getDoc, orderBy, query, writeBatch, where, serverTimestamp 
} from "firebase/firestore"; 
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import './AdminDashboard.css';

const isTenderClosed = (dateString, timeString) => {
  if (!dateString) return false;
  const time = timeString || "18:30"; // Fallback for older tenders
  const closeTime = new Date(`${dateString}T${time}:00+05:30`);
  const now = new Date();
  return now > closeTime;
};

function AdminDashboard() {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('menu');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setAuthLoading(false);
      } else {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = () => { signOut(auth); navigate('/'); };
  const goToHome = () => { navigate('/'); };

  if (authLoading) return <div style={{padding:'50px', textAlign:'center'}}>Loading Dashboard...</div>;

  // --- MENU VIEW ---
  if (currentView === 'menu') {
    return (
      <div className="admin-wrapper">
        <header className="admin-header">
          <div className="container header-flex">
            <h2>Admin Dashboard</h2>
            <div className="header-actions">
              <button onClick={goToHome} className="btn-icon">🏠 Home Page</button>
              <button onClick={() => setCurrentView('settings')} className="btn-icon">⚙️ Password</button>
              <button onClick={handleLogout} className="btn-logout">Logout</button>
            </div>
          </div>
        </header>

        <div className="container dashboard-grid fade-in-up">
          <div className="dash-card" onClick={() => setCurrentView('tenders')}>
            <div className="card-icon gradient-1">📄</div>
            <div className="card-info">
              <h3>E-Tendering</h3>
              <p>Publish, Manage & View Results</p>
            </div>
          </div>

          <div className="dash-card" onClick={() => setCurrentView('committee')}>
            <div className="card-icon gradient-2">👥</div>
            <div className="card-info">
              <h3>Committee</h3>
              <p>Add, Remove & Reorder</p>
            </div>
          </div>

          <div className="dash-card" onClick={() => setCurrentView('announcements')}>
            <div className="card-icon" style={{background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)'}}>📢</div>
            <div className="card-info">
              <h3>Announcements</h3>
              <p>Latest News & PDFs</p>
            </div>
          </div>

          {/* NEW MEMBERS BUTTON */}
          <div className="dash-card" onClick={() => setCurrentView('members')}>
            <div className="card-icon" style={{background: 'linear-gradient(135deg, #1abc9c, #16a085)'}}>🏠</div>
            <div className="card-info">
              <h3>Members Directory</h3>
              <p>Manage Property Owners & Details</p>
            </div>
          </div>

          {/* NEW RATE PLANS BUTTON */}
          <div className="dash-card" onClick={() => setCurrentView('rates')}>
            <div className="card-icon" style={{background: 'linear-gradient(135deg, #f39c12, #d35400)'}}>💰</div>
            <div className="card-info">
              <h3>Rate & Billing Plans</h3>
              <p>Configure Charges & Penalties</p>
            </div>
          </div>

          {/* NEW LEDGER BUTTON */}
          <div className="dash-card" onClick={() => navigate('/admin/ledger')}>
            <div className="card-icon" style={{background: 'linear-gradient(135deg, #9b59b6, #8e44ad)'}}>📊</div>
            <div className="card-info">
              <h3>Master Ledger</h3>
              <p>Financials, Defaulters & Reports</p>
            </div>
          </div>

          <div className="dash-card" onClick={() => navigate('/admin/manual-payment')}>
            <div className="card-icon" style={{background: 'linear-gradient(135deg, #27ae60, #2ecc71)'}}>💵</div>
            <div className="card-info">
              <h3>Manual Payments</h3>
              <p>Clear Physical Bills & Cash Receipts</p>
            </div>
          </div>

          {/* NEW VERIFIED CONTACTS BUTTON */}
          <div className="dash-card" onClick={() => navigate('/admin/contacts')}>
            <div className="card-icon" style={{background: 'linear-gradient(135deg, #0097e6, #00a8ff)'}}>👷‍♂️</div>
            <div className="card-info">
              <h3>Verified Contacts</h3>
              <p>Manage Staff, Guards & Services</p>
            </div>
          </div>

          {/* NEW DIRECT MESSAGES BUTTON */}
          <div className="dash-card" onClick={() => navigate('/admin/messages')}>
            <div className="card-icon" style={{background: 'linear-gradient(135deg, #e056fd, #eb4d4b)'}}>✉️</div>
            <div className="card-info">
              <h3>Direct Messages</h3>
              <p>Send Alerts & App Notifications</p>
            </div>
          </div>

        </div>
      </div>
    );
  }

  // --- SUB VIEWS HEADER ---
  let viewTitle = "";
  if (currentView === 'tenders') viewTitle = "Manage Tenders";
  else if (currentView === 'committee') viewTitle = "Manage Committee";
  else if (currentView === 'announcements') viewTitle = "Manage Announcements";
  else if (currentView === 'members') viewTitle = "Manage Members";
  else if (currentView === 'rates') viewTitle = "Billing Rate Plans"; // <--- ADD THIS LINE
  else viewTitle = "Settings";

  return (
    <div className="admin-wrapper">
      <header className="admin-header">
        <div className="container header-flex">
          <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
            <button onClick={() => setCurrentView('menu')} className="btn-back">← Back to Menu</button>
            <button onClick={goToHome} className="btn-icon">🏠 Home Page</button>
          </div>
          <h2>{viewTitle}</h2>
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </header>
      
      <div className="container fade-in-up">
        {currentView === 'tenders' && <TenderManager />}
        {currentView === 'committee' && <CommitteeManager />}
        {currentView === 'announcements' && <AnnouncementManager />}
        {currentView === 'members' && <MemberManager />}
        {currentView === 'rates' && <RatePlanManager />} {/* <--- ADD THIS LINE */}
        {currentView === 'settings' && <SettingsManager currentUser={user} />}
      </div>
    </div>
  );
}

// ==========================================
// NEW: MEMBER MANAGER
// ==========================================
// ==========================================
// UPDATED: MEMBER MANAGER
// ==========================================
function MemberManager() {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [membersData, setMembersData] = useState({
    houses: [], shops: [], booths: [], c_buildings: [], n_home: []
  });

  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});

  const propertyConfigs = [
    { key: 'houses', title: 'Houses', color: '#3498db' },
    { key: 'shops', title: 'Shops', color: '#e67e22' },
    { key: 'booths', title: 'Booths', color: '#9b59b6' },
    { key: 'c_buildings', title: 'Commercial Buildings', color: '#34495e' },
    { key: 'n_home', title: 'Nursing Homes', color: '#16a085' }
  ];

  const fetchAllMembers = async () => {
    setLoading(true);
    const newData = { houses: [], shops: [], booths: [], c_buildings: [], n_home: [] };
    
    try {
      await Promise.all(propertyConfigs.map(async (config) => {
        const snap = await getDocs(collection(db, config.key));
        
        // --- PROPER NUMERICAL SORTING ALGORITHM ---
        const sortedDocs = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const plotA = String(a['Plot no'] || a.id || "");
            const plotB = String(b['Plot no'] || b.id || "");
            // localeCompare with numeric: true treats "10" as greater than "2"
            return plotA.localeCompare(plotB, undefined, { numeric: true, sensitivity: 'base' });
          });

        newData[config.key] = sortedDocs;
      }));
      setMembersData(newData);
    } catch (error) {
      console.error("Error fetching members:", error);
      alert("Failed to load members.");
    }
    setLoading(false);
  };

  useEffect(() => { 
    fetchAllMembers(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditModal = (member, propertyType) => {
    setEditingMember({ ...member, propertyType });
    setEditForm({
      ownerName: member.ownerName || member.name || "",
      phone: member.phone || member.mobile || "",
      address: member.address || "",
      plotSize: member.plotSize || member.size_yards || "",
      uidNo: member.uidNo || member.uid || "",
      status: member.status || "ACTIVE",
      canPay: member.canPay !== false, 
      isFilled: member.isFilled === true 
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const docRef = doc(db, editingMember.propertyType, editingMember.id);
      await updateDoc(docRef, {
        ...editForm,
        updatedAt: serverTimestamp()
      });
      
      alert("Member details updated successfully!");
      setEditingMember(null);
      fetchAllMembers(); 
    } catch (error) {
      alert("Error updating member: " + error.message);
    }
  };

  if (loading) return <div style={{textAlign: 'center', padding: '40px'}}>Loading all properties...</div>;

  return (
    <div>
      <input 
        type="text" 
        className="member-search-bar" 
        placeholder="🔍 Search by Plot No, Name, or Phone..." 
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="member-grid">
        {propertyConfigs.map(config => {
          const filteredMembers = membersData[config.key].filter(m => {
            const searchStr = searchTerm.toLowerCase();
            const plotMatch = (m['Plot no'] || m.id).toLowerCase().includes(searchStr);
            const nameMatch = (m.ownerName || m.name || "").toLowerCase().includes(searchStr);
            const phoneMatch = (m.phone || m.mobile || "").toLowerCase().includes(searchStr);
            return plotMatch || nameMatch || phoneMatch;
          });

          return (
            <div key={config.key} className="member-box">
              <h3 style={{background: config.color}}>
                <span>{config.title}</span>
                <span style={{fontSize:'12px', background:'rgba(0,0,0,0.2)', padding:'2px 8px', borderRadius:'10px'}}>
                  {filteredMembers.length}
                </span>
              </h3>
              
              <ul className="member-list">
                {filteredMembers.length === 0 ? (
                  <p style={{textAlign:'center', padding:'20px', color:'#999'}}>No matches found.</p>
                ) : (
                  filteredMembers.map(member => (
                    <li key={member.id} className="member-card">
                      <div className="member-card-header">
                        <div className="member-info">
                          <h4>Plot #{member['Plot no'] || member.id} - {member.ownerName || member.name || "N/A"}</h4>
                          <div className="badges-row">
                            <span className={`badge-status ${member.status === 'INACTIVE' ? 'badge-inactive' : 'badge-active'}`}>
                              {member.status || 'ACTIVE'}
                            </span>
                            
                            {/* NEW: Hide Constructed, Show Red/Blue 'Not Constructed' */}
                            {!member.isFilled && <span className="badge-not-constructed">🏠 Not Constructed</span>}
                            
                            {member.canPay === false && <span className="badge-warning">🚫 Exempt</span>}
                          </div>
                        </div>
                        <button onClick={() => openEditModal(member, config.key)} className="btn-small edit">Edit</button>
                      </div>

                      <div className="member-details-grid">
                        <p><strong>Phone:</strong> {member.phone || member.mobile || "N/A"}</p>
                        <p><strong>UID:</strong> {member.uidNo || member.uid || "N/A"}</p>
                        <p><strong>Size:</strong> {member.plotSize || member.size_yards ? `${member.plotSize || member.size_yards} sq.yd` : "N/A"}</p>
                        <p className="full-width"><strong>Address:</strong> {member.address || "N/A"}</p>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          );
        })}
      </div>

      {/* EDIT MODAL */}
      {editingMember && (
        <div className="modal-overlay">
          <div className="modal-box" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <h3>Edit Details: Plot #{editingMember['Plot no'] || editingMember.id}</h3>
              <button onClick={() => setEditingMember(null)} className="btn-close">✖</button>
            </div>
            
            <form onSubmit={handleUpdate} className="modal-body">
              <div className="row-inputs">
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', color:'#666'}}>Owner Name</label>
                  <input className="input-field" value={editForm.ownerName} onChange={e => setEditForm({...editForm, ownerName: e.target.value})} required />
                </div>
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', color:'#666'}}>Phone Number</label>
                  <input className="input-field" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                </div>
              </div>

              <label style={{fontSize:'12px', color:'#666'}}>Address</label>
              <textarea className="input-field" rows="2" value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />

              <div className="row-inputs">
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', color:'#666'}}>Plot Size (Sq Yards)</label>
                  <input className="input-field" value={editForm.plotSize} onChange={e => setEditForm({...editForm, plotSize: e.target.value})} />
                </div>
                <div className="row-inputs">
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', color:'#666'}}>Login Password</label>
                  <input 
                    className="input-field" 
                    placeholder="Set Resident Password" 
                    value={editForm.password || ""} 
                    onChange={e => setEditForm({...editForm, password: e.target.value})} 
                  />
                </div>
              </div>
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', color:'#666'}}>UID Number</label>
                  <input className="input-field" value={editForm.uidNo} onChange={e => setEditForm({...editForm, uidNo: e.target.value})} />
                </div>
              </div>

              <div className="row-inputs">
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', color:'#666'}}>Account Status</label>
                  <select className="input-field" value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              </div>

              {/* NEW: Structured & Spaced Checkbox Group */}
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={editForm.isFilled} onChange={e => setEditForm({...editForm, isFilled: e.target.checked})} />
                  🏠 Property is Constructed
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={editForm.canPay} onChange={e => setEditForm({...editForm, canPay: e.target.checked})} />
                  ✅ Eligible to Pay Bills
                </label>
              </div>

              <button type="submit" className="btn-primary-full">Save Changes</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ... (KEEP YOUR EXISTING AnnouncementManager, TenderManager, CommitteeManager, and SettingsManager EXACTLY AS THEY ARE BELOW THIS LINE) ...

// --- 1. ANNOUNCEMENT MANAGER ---
function AnnouncementManager() {
  const [loading, setLoading] = useState(false);
  const [notices, setNotices] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState("");
  
  const [notice, setNotice] = useState({ title: "", description: "", date: "", file: null });

  const fetchNotices = async () => {
    const q = query(collection(db, "announcements"), orderBy("date", "desc"));
    const querySnapshot = await getDocs(q);
    setNotices(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => { fetchNotices(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let fileUrl = existingFileUrl;
      if (notice.file) {
        const fileRef = ref(storage, `announcements/${Date.now()}_${notice.file.name}`);
        const snapshot = await uploadBytes(fileRef, notice.file);
        fileUrl = await getDownloadURL(snapshot.ref);
      }
      
      const data = { 
        title: notice.title, 
        description: notice.description, 
        date: notice.date, 
        fileUrl, 
        createdAt: new Date() 
      };

      if (editingId) {
        await updateDoc(doc(db, "announcements", editingId), data);
        setEditingId(null);
        alert("Announcement Updated!");
      } else {
        await addDoc(collection(db, "announcements"), data);
        alert("Announcement Published!");
      }
      setNotice({ title: "", description: "", date: "", file: null });
      setExistingFileUrl("");
      fetchNotices();
    } catch (err) { alert(err.message); } 
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if(window.confirm("Delete this announcement?")) {
      await deleteDoc(doc(db, "announcements", id));
      fetchNotices();
    }
  };

  const handleEdit = (n) => {
    setEditingId(n.id);
    setExistingFileUrl(n.fileUrl || "");
    setNotice({ title: n.title, description: n.description, date: n.date, file: null });
    window.scrollTo(0,0);
  };

  return (
    <div className="manager-layout">
      {/* Form */}
      <div className="form-card floating-card">
        <h3>{editingId ? "Edit Announcement" : "Post New Announcement"}</h3>
        <form onSubmit={handleSubmit}>
          <input className="input-field" placeholder="Title" value={notice.title} onChange={e => setNotice({...notice, title: e.target.value})} required />
          <textarea className="input-field" placeholder="Description" rows="3" value={notice.description} onChange={e => setNotice({...notice, description: e.target.value})} required />
          <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Date Displayed:</label>
          <input type="date" className="input-field" value={notice.date} onChange={e => setNotice({...notice, date: e.target.value})} required />
          <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>PDF (Optional):</label>
          <input type="file" className="input-field" accept="application/pdf" onChange={e => setNotice({...notice, file: e.target.files[0]})} />
          
          <button type="submit" disabled={loading} className="btn-primary-full">{loading ? "Processing..." : editingId ? "Update" : "Publish"}</button>
          {editingId && <button type="button" onClick={() => {setEditingId(null); setNotice({ title: "", description: "", date: "", file: null });}} className="btn-text">Cancel Edit</button>}
        </form>
      </div>

      {/* List */}
      <div className="list-card floating-card">
        <h3>Live Announcements</h3>
        <div className="list-scroll">
          {notices.map(n => (
            <div key={n.id} className="list-item">
              <div>
                <strong>{n.title}</strong>
                <br/><small>{n.date}</small>
              </div>
              <div className="item-actions">
                <button onClick={() => handleEdit(n)} className="btn-small edit">Edit</button>
                <button onClick={() => handleDelete(n.id)} className="btn-small delete">Del</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// NEW: RATE PLAN MANAGER
// ==========================================
function RatePlanManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('houses');
  const [plans, setPlans] = useState({});
  
  // --- NEW STATES FOR YEARLY PENALTY ---
  const [yearlyStats, setYearlyStats] = useState([]);
  const [fetchingStats, setFetchingStats] = useState(false);

  const tabs = [
    { id: 'houses', label: 'Houses' },
    { id: 'shops', label: 'Shops' },
    { id: 'booths', label: 'Booths' },
    { id: 'c_buildings', label: 'Commercial Bldgs' },
    { id: 'n_home', label: 'Nursing Homes' }
  ];

  // Fetch all 5 rate plans
  const fetchRatePlans = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "rates_plan"));
      const fetchedPlans = {};
      snap.forEach(doc => { fetchedPlans[doc.id] = doc.data(); });
      setPlans(fetchedPlans);
    } catch (err) {
      alert("Error fetching rate plans: " + err.message);
    }
    setLoading(false);
  };

  // --- NEW FUNCTION TO FETCH YEARLY BILL STATS ---
  const fetchYearlyStats = async () => {
    setFetchingStats(true);
    try {
      const q = query(collection(db, 'bills'), where('billType', '==', 'YEARLY'));
      const snap = await getDocs(q);
      const stats = {};
      
      snap.forEach(doc => {
        const b = doc.data();
        if (!stats[b.year]) stats[b.year] = { total: 0, unpaidTotal: 0, unpaidEligible: 0, appliedPenaltyAmount: 0, penalizedBaseAmount: 0 };
        stats[b.year].total += 1;
        
        if (b.status === 'UNPAID' && (b.totalPayable || 0) > 0) {
          stats[b.year].unpaidTotal += 1;
          
          if (!b.penalty?.maintApplied) {
            stats[b.year].unpaidEligible += 1;
          } else {
            stats[b.year].appliedPenaltyAmount += (b.penalty?.amount || 0);
            stats[b.year].penalizedBaseAmount += (b.totalPayable || 0); // Capture base to calculate % later
          }
        }
      });
      
      const formattedStats = Object.keys(stats).map(y => ({ year: y, ...stats[y] })).sort((a,b) => b.year.localeCompare(a.year));
      setYearlyStats(formattedStats);
      
      const firstUnpaid = formattedStats.find(s => s.unpaidTotal > 0);
      setPlans(prev => ({
        ...prev, 
        selectedYearForPenalty: firstUnpaid ? firstUnpaid.year : (formattedStats[0]?.year || new Date().getFullYear().toString()), 
        yearlyPenaltyType: 'PERCENT' 
      }));
    } catch(e) { console.error(e); }
    setFetchingStats(false);
  };

  useEffect(() => {
    fetchRatePlans();
    fetchYearlyStats(); // Trigger the new fetch function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle deep object state updates (supports nested sizes like '100.s_w')
  const handleChange = (section, field, value, type = 'string') => {
    let finalValue = value;
    if (type === 'number') finalValue = Number(value);
    
    setPlans(prev => {
      const updatedSection = { ...(prev[activeTab]?.[section] || {}) };
      
      if (field.includes('.')) {
        const [subKey, subField] = field.split('.');
        updatedSection[subKey] = {
          ...(updatedSection[subKey] || {}),
          [subField]: finalValue
        };
      } else {
        updatedSection[field] = finalValue;
      }

      return {
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          [section]: updatedSection
        }
      };
    });
  };

  // Handle Top-Level state updates (e.g. effectiveFromMonth)
  const handleTopLevelChange = (field, value) => {
    setPlans(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: value
      }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dataToSave = {
        ...plans[activeTab],
        updatedAt: serverTimestamp(),
        createdByAdminId: auth.currentUser.uid // Track who changed it
      };
      
      await updateDoc(doc(db, "rates_plan", activeTab), dataToSave);
      alert(`✅ ${tabs.find(t=>t.id === activeTab).label} Rate Plan updated securely!`);
    } catch (err) {
      alert("Error saving: " + err.message);
    }
    setSaving(false);
  };

  // --- NEW DIRECT PENALTY APPLY FUNCTION ---
  const applyYearlyPenalty = async () => {
    const year = plans.selectedYearForPenalty;
    const penaltyValue = Number(plans.yearlyPenaltyValue);
    const penaltyType = plans.yearlyPenaltyType || 'PERCENT';

    if (!year) return alert("Please select a year.");
    if (!penaltyValue || penaltyValue <= 0) return alert("Please enter a valid penalty amount or percentage.");

    const targetStat = yearlyStats.find(s => s.year === year);
    if (!targetStat || targetStat.unpaidEligible === 0) return alert("No eligible bills found for this year.");

    if (!window.confirm(`Apply ${penaltyType === 'PERCENT' ? penaltyValue + '%' : '₹' + penaltyValue} penalty to ${targetStat.unpaidEligible} properties for the year ${year}?`)) return;

    setSaving(true);
    try {
        const q = query(collection(db, 'bills'), where('billType', '==', 'YEARLY'), where('year', '==', year), where('status', '==', 'UNPAID'));
        const billsSnap = await getDocs(q);

        const batch = writeBatch(db);
        let count = 0;

        billsSnap.forEach(docSnap => {
            const b = docSnap.data();
            const maint = b.totalPayable || 0;
            
            if (maint > 0 && !b.penalty?.maintApplied) {
                let pAmt = penaltyType === 'PERCENT' ? Math.round(maint * (penaltyValue / 100)) : penaltyValue;

                if (pAmt > 0) {
                    const ref = doc(db, 'bills', docSnap.id);
                    batch.update(ref, {
                        'penalty.amount': (b.penalty?.amount || 0) + pAmt,
                        'penalty.maintApplied': true,
                        'penalty.maintAppliedAt': serverTimestamp()
                    });
                    count++;
                }
            }
        });

        if (count > 0) {
            await batch.commit();
            alert(`✅ Success! Penalty permanently applied to ${count} bills for the year ${year}.`);
            setPlans(prev => ({...prev, yearlyPenaltyValue: ''}));
            fetchYearlyStats(); // Refresh the list
        } else {
            alert("No eligible bills found during processing.");
        }
    } catch (error) {
        console.error(error);
        alert("Error applying penalty: " + error.message);
    }
    setSaving(false);
  };

  if (loading) return <div style={{padding:'40px', textAlign:'center'}}>Loading Rate Plans...</div>;

  const currentPlan = plans[activeTab] || {};
  const comp = currentPlan.components || {};
  const pen = currentPlan.penaltyRule || {};
  const adv = currentPlan.advanceBenefitRule || {};
  const plat = currentPlan.platformChargeRule || {};

  return (
    <div className="rate-plan-container">
      {/* TABS HEADER */}
      <div className="rate-tabs">
        {tabs.map(tab => (
          <div 
            key={tab.id} 
            className={`rate-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
        {/* SPECIAL YEARLY PENALTY TAB */}
        <div 
          className={`rate-tab ${activeTab === 'yearly_penalty' ? 'active' : ''}`}
          onClick={() => setActiveTab('yearly_penalty')}
          style={{ color: '#c0392b', fontWeight: 'bold' }}
        >
          YEARLY Maintenance.
        </div>
      </div>

      {activeTab === 'yearly_penalty' ? (
         <div className="rate-form-area fade-in-up">
            <div className="rate-box" style={{ borderLeft: '5px solid #c0392b' }}>
              <h4 style={{color: '#c0392b', fontSize: '18px', borderBottom: 'none', marginBottom: '5px', display: 'block'}}>Yearly Maintenance Penalty</h4>
              <p style={{fontSize: '13px', color: '#666', marginBottom: '20px'}}>
                Select a previous or current year to apply penalties to unconstructed properties with unpaid maintenance bills.
              </p>
              
              {fetchingStats ? <div style={{padding: '20px', color: '#666'}}>⏳ Loading yearly bills data...</div> : (() => {
                const selectedStat = yearlyStats.find(s => s.year === plans.selectedYearForPenalty);
                const isPenaltyAlreadyApplied = selectedStat && selectedStat.unpaidTotal > 0 && selectedStat.unpaidEligible === 0;

                return (
                <>
                  <div style={{marginBottom: '15px'}}>
                    <label style={{fontSize: '12px', fontWeight: 'bold'}}>Select Maintenance Year</label>
                    <select 
                      className="input-field" 
                      style={{marginBottom: 0, maxWidth: '350px'}} 
                      value={plans.selectedYearForPenalty || ''} 
                      onChange={e => setPlans(prev => ({...prev, selectedYearForPenalty: e.target.value}))}
                    >
                      {yearlyStats.length === 0 && <option value="">No yearly bills found</option>}
                      {yearlyStats.map(stat => (
                        <option key={stat.year} value={stat.year} disabled={stat.unpaidTotal === 0}>
                          October {stat.year} to September {parseInt(stat.year) + 1}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Dedicated Alert Box for Unpaid Users */}
                  {selectedStat && selectedStat.unpaidTotal > 0 && (
                    <div style={{
                      background: isPenaltyAlreadyApplied ? '#e8f5e9' : '#fff3e0', 
                      border: `1px solid ${isPenaltyAlreadyApplied ? '#c8e6c9' : '#ffe0b2'}`, 
                      padding: '10px 15px', 
                      borderRadius: '6px', 
                      marginBottom: '20px',
                      display: 'inline-block'
                    }}>
                      <span style={{color: isPenaltyAlreadyApplied ? '#2e7d32' : '#e65100', fontWeight: 'bold', fontSize: '13px'}}>
                        {isPenaltyAlreadyApplied 
                          ? `✅ A penalty of approx ${Math.round((selectedStat.appliedPenaltyAmount / (selectedStat.penalizedBaseAmount || 1)) * 100)}% has already been applied and total ${selectedStat.unpaidTotal} bills are still pending.` 
                          : `⚠️ ${selectedStat.unpaidEligible} properties have unpaid bills for this period and are eligible for penalty.`}
                      </span>
                    </div>
                  )}

                  <div style={{ 
                      display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap',
                      opacity: isPenaltyAlreadyApplied ? 0.5 : 1, 
                      pointerEvents: isPenaltyAlreadyApplied ? 'none' : 'auto'
                  }}>
                     <div>
                        <label style={{fontSize: '12px', fontWeight: 'bold'}}>Penalty Type</label>
                        <select 
                           className="input-field" 
                           style={{marginBottom: 0, width: '180px'}} 
                           value={plans.yearlyPenaltyType || 'PERCENT'} 
                           onChange={e => setPlans(prev => ({...prev, yearlyPenaltyType: e.target.value}))}
                        >
                           <option value="PERCENT">Percentage (%)</option>
                           <option value="FLAT">Fixed Amount (₹)</option>
                        </select>
                     </div>
                     <div>
                        <label style={{fontSize: '12px', fontWeight: 'bold'}}>
                          {plans.yearlyPenaltyType === 'FLAT' ? 'Amount (₹)' : 'Percentage (%)'}
                        </label>
                        <input 
                           type="number"
                           className="input-field" 
                           style={{marginBottom: 0, width: '180px'}} 
                           value={plans.yearlyPenaltyValue || ''} 
                           onChange={e => setPlans(prev => ({...prev, yearlyPenaltyValue: e.target.value}))}
                           placeholder={plans.yearlyPenaltyType === 'FLAT' ? "e.g. 2000" : "e.g. 25"}
                        />
                     </div>
                  </div>

                  <button 
                     onClick={applyYearlyPenalty} 
                     disabled={saving || isPenaltyAlreadyApplied || !selectedStat || selectedStat.unpaidEligible === 0}
                     className="btn-primary-full" 
                     style={{
                       width: 'auto', 
                       background: (isPenaltyAlreadyApplied || !selectedStat || selectedStat.unpaidEligible === 0) ? '#95a5a6' : '#c0392b', 
                       boxShadow: 'none',
                       cursor: (isPenaltyAlreadyApplied || !selectedStat || selectedStat.unpaidEligible === 0) ? 'not-allowed' : 'pointer'
                     }}
                  >
                     {saving ? "⏳ Processing..." : isPenaltyAlreadyApplied ? "✅ Penalty Already Applied" : "⚠️ Apply Penalty Directly"}
                  </button>
                </>
              );
            })()}
            </div>
         </div>
      ) : (
      <>
      {/* ACTIVE FORM */}
      <form className="rate-form-area" onSubmit={handleSave}>
        
        <div className="rate-header-row">
          <div>
            <h3 style={{margin: '0 0 5px 0', color: '#333'}}>
              Configuring: <span style={{color: 'var(--primary)'}}>{tabs.find(t=>t.id === activeTab).label}</span>
            </h3>
            <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
              <label style={{fontSize:'12px', color:'#666', fontWeight:'bold'}}>Effective From Month:</label>
              <input 
                type="month" 
                className="rate-select-small" 
                value={currentPlan.effectiveFromMonth || ''} 
                onChange={(e) => handleTopLevelChange('effectiveFromMonth', e.target.value)}
                required
              />
            </div>
          </div>
          <button type="submit" className="btn-primary-full" style={{width: '200px'}} disabled={saving}>
            {saving ? "Saving..." : "💾 Save Plan"}
          </button>
        </div>

        <div className="rate-section-grid">
          
          {/* BOX 1: COMPONENTS */}
          <div className="rate-box" style={{ gridRow: (activeTab === 'houses' || activeTab === 'n_home') ? 'span 2' : 'auto' }}>
            <h4>🧾 Base Components (Monthly)</h4>
            
            {activeTab === 'houses' ? (
              ['100', '200', '300', '400', '500'].map(size => (
                <div key={size} style={{marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px dashed #eee'}}>
                  <strong style={{color: '#555'}}>{size} Sq. Yards</strong>
                  <div className="rate-input-row" style={{marginTop: '5px'}}>
                    <label>Sewage & Water (₹)</label>
                    <input type="number" className="rate-input-small" value={comp[size]?.s_w ?? 0} onChange={(e) => handleChange('components', `${size}.s_w`, e.target.value, 'number')} />
                  </div>
                  <div className="rate-input-row">
                    <label>Service Charge (₹)</label>
                    <input type="number" className="rate-input-small" value={comp[size]?.service ?? 0} onChange={(e) => handleChange('components', `${size}.service`, e.target.value, 'number')} />
                  </div>
                  <div className="rate-input-row">
                    <label>Maintenance (₹ / Yearly)</label>
                    <input type="number" className="rate-input-small" value={comp[size]?.maintenance ?? 0} onChange={(e) => handleChange('components', `${size}.maintenance`, e.target.value, 'number')} />
                  </div>
                </div>
              ))
            ) : activeTab === 'n_home' ? (
              ['54', '61'].map(id => (
                <div key={id} style={{marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px dashed #eee'}}>
                  <strong style={{color: '#555'}}>Property #{id}</strong>
                  <div className="rate-input-row" style={{marginTop: '5px'}}>
                    <label>Sewage & Water (₹)</label>
                    <input type="number" className="rate-input-small" value={comp[id]?.s_w ?? 0} onChange={(e) => handleChange('components', `${id}.s_w`, e.target.value, 'number')} />
                  </div>
                  <div className="rate-input-row">
                    <label>Service Charge (₹)</label>
                    <input type="number" className="rate-input-small" value={comp[id]?.service ?? 0} onChange={(e) => handleChange('components', `${id}.service`, e.target.value, 'number')} />
                  </div>
                  <div className="rate-input-row">
                    <label>Maintenance (₹ / Yearly)</label>
                    <input type="number" className="rate-input-small" value={comp[id]?.maintenance ?? 0} onChange={(e) => handleChange('components', `${id}.maintenance`, e.target.value, 'number')} />
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="rate-input-row">
                  <label>Sewage & Water (₹)</label>
                  <input type="number" className="rate-input-small" value={comp.s_w ?? 0} onChange={(e) => handleChange('components', 's_w', e.target.value, 'number')} />
                </div>
                <div className="rate-input-row">
                  <label>Service Charge (₹)</label>
                  <input type="number" className="rate-input-small" value={comp.service ?? 0} onChange={(e) => handleChange('components', 'service', e.target.value, 'number')} />
                </div>
                <div className="rate-input-row">
                  <label>Maintenance (₹ / Yearly)</label>
                  <input type="number" className="rate-input-small" value={comp.maintenance ?? 0} onChange={(e) => handleChange('components', 'maintenance', e.target.value, 'number')} />
                </div>
              </>
            )}
          </div>

          {/* BOX 2: PENALTY RULE */}
          <div className="rate-box">
            <h4>⚠️ Late Penalty Rule</h4>
            <div className="rate-input-row">
              <label>Enable Penalties?</label>
              <input type="checkbox" style={{width:'18px', height:'18px'}} checked={pen.enabled || false} onChange={(e) => handleChange('penaltyRule', 'enabled', e.target.checked, 'boolean')} />
            </div>
            <div className="rate-input-row">
              <label>Penalty Type</label>
              <select className="rate-select-small" value={pen.type || 'PERCENT'} onChange={(e) => handleChange('penaltyRule', 'type', e.target.value)}>
                <option value="PERCENT">Percentage (%)</option>
                <option value="FLAT">Flat Rate (₹)</option>
              </select>
            </div>
            <div className="rate-input-row">
              <label>Penalty Value</label>
              <input type="number" className="rate-input-small" value={pen.value ?? 0} onChange={(e) => handleChange('penaltyRule', 'value', e.target.value, 'number')} />
            </div>
            <div className="rate-input-row" style={{marginTop:'10px', paddingTop:'10px', borderTop:'1px dashed #eee'}}>
              <label style={{fontSize:'11px', color:'#999'}}>Apply after 18th automatically?</label>
              <input type="checkbox" checked={pen.applyAfterDueDate || false} onChange={(e) => handleChange('penaltyRule', 'applyAfterDueDate', e.target.checked, 'boolean')} />
            </div>
          </div>

          {/* BOX 3: ADVANCE BENEFITS */}
          <div className="rate-box">
            <h4>🎁 Advance Payment Benefits</h4>
            <div className="rate-input-row">
              <label>Enable Benefits?</label>
              <input type="checkbox" style={{width:'18px', height:'18px'}} checked={adv.enabled || false} onChange={(e) => handleChange('advanceBenefitRule', 'enabled', e.target.checked, 'boolean')} />
            </div>
            <div className="rate-input-row">
              <label>Discount Type</label>
              <select className="rate-select-small" value={adv.type || 'PERCENT'} onChange={(e) => handleChange('advanceBenefitRule', 'type', e.target.value)}>
                <option value="PERCENT">Percentage (%)</option>
                <option value="FLAT">Flat Rate (₹)</option>
              </select>
            </div>
            <div className="rate-input-row">
              <label>Discount Value</label>
              <input type="number" className="rate-input-small" value={adv.value ?? 0} onChange={(e) => handleChange('advanceBenefitRule', 'value', e.target.value, 'number')} />
            </div>
            <div className="rate-input-row">
              <label>Min. Months Required</label>
              <input type="number" className="rate-input-small" style={{width:'60px'}} value={adv.minAdvanceMonths ?? 1} onChange={(e) => handleChange('advanceBenefitRule', 'minAdvanceMonths', e.target.value, 'number')} />
            </div>
            <div className="rate-input-row">
              <label>Max. Months Allowed</label>
              <input type="number" className="rate-input-small" style={{width:'60px'}} value={adv.maxAdvanceMonths ?? 12} onChange={(e) => handleChange('advanceBenefitRule', 'maxAdvanceMonths', e.target.value, 'number')} />
            </div>
          </div>

          {/* BOX 4: PLATFORM CHARGES */}
          <div className="rate-box">
            <h4>💳 Gateway Platform Charges</h4>
            <div className="rate-input-row">
              <label>Enable Gateway Fee?</label>
              <input type="checkbox" style={{width:'18px', height:'18px'}} checked={plat.enabled || false} onChange={(e) => handleChange('platformChargeRule', 'enabled', e.target.checked, 'boolean')} />
            </div>
            <div className="rate-input-row">
              <label>Who pays the fee?</label>
              <select className="rate-select-small" value={plat.payerSide ? 'USER' : 'SOCIETY'} onChange={(e) => handleChange('platformChargeRule', 'payerSide', e.target.value === 'USER', 'boolean')}>
                <option value="USER">Resident Pays</option>
                <option value="SOCIETY">Society Absorbs</option>
              </select>
            </div>
            <div className="rate-input-row">
              <label>Percentage Fee (%)</label>
              <input type="number" step="0.1" className="rate-input-small" value={plat.percent ?? 0} onChange={(e) => handleChange('platformChargeRule', 'percent', e.target.value, 'number')} />
            </div>
            <div className="rate-input-row">
              <label>Flat Fee (₹)</label>
              <input type="number" className="rate-input-small" value={plat.flat ?? 0} onChange={(e) => handleChange('platformChargeRule', 'flat', e.target.value, 'number')} />
            </div>
          </div>

        </div>
      </form>
      </>
      )}
    </div>
  );
}

// --- 2. TENDER MANAGER (UPDATED WITH RESULTS) ---
function TenderManager() {
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [tenders, setTenders] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState("");
  const [tender, setTender] = useState({ title: "", description: "", openDate: "", closeDate: "", closeTime: "18:00", file: null });
  // --- NEW: Mandatory Checklist State ---
  const [checklist, setChecklist] = useState([false, false, false, false, false, false, false]);
  const allChecked = checklist.every(Boolean);

  const checklistItems = [
    'ਫਾਈਲ ਸਾਈਜ਼ ਲਿਮਿਟ (File Size Limit): ਮੈਂ ਤਸਦੀਕ ਕਰਦਾ ਹਾਂ ਕਿ ਅਪਲੋਡ ਕੀਤਾ ਗਿਆ ਟੈਂਡਰ ਦਸਤਾਵੇਜ਼ ਅਤੇ ਸਾਰੇ ਨਾਲ ਨੱਥੀ ਕਾਗਜ਼ਾਤ (Annexures) ਕੁੱਲ ਮਿਲਾ ਕੇ 15MB ਤੋਂ ਘੱਟ ਹਨ।',
    'ਅੰਤਮ ਲਾਗਤ ਦੀ ਪਰਿਭਾਸ਼ਾ (Final Cost Definition): ਮੈਂ ਦਸਤਾਵੇਜ਼ ਵਿੱਚ "ਫਾਈਨਲ ਬਿਡ ਕੋਸਟ" (Final Bid Cost) ਦੇ ਦਾਇਰੇ ਨੂੰ ਸਪੱਸ਼ਟ ਰੂਪ ਵਿੱਚ ਪਰਿਭਾਸ਼ਿਤ ਕੀਤਾ ਹੈ। ਮੈਂ ਸਪੱਸ਼ਟ ਕੀਤਾ ਹੈ ਕਿ ਬੋਲੀ ਸਿਰਫ ਸਮਾਨ ਦੀ ਸਪਲਾਈ ਲਈ ਹੈ, ਜਾਂ ਇਸ ਵਿੱਚ ਸਪਲਾਈ + ਢੋਆ-ਢੁਆਈ + ਇੰਸਟਾਲੇਸ਼ਨ + ਟੈਸਟਿੰਗ + ਕਮਿਸ਼ਨਿੰਗ + ਰੱਖ-ਰਖਾਅ (Maintenance) ਸਭ ਕੁਝ ਸ਼ਾਮਲ ਹੈ।',
    'ਟੈਕਸ ਨਿਯਮ (Tax Implications): ਮੈਂ ਸਪੱਸ਼ਟ ਤੌਰ \'ਤੇ ਦੱਸਿਆ ਹੈ ਕਿ ਵੈਂਡਰਾਂ ਦੀ ਅੰਤਮ ਵਿੱਤੀ ਬੋਲੀ (Financial bid) ਵਿੱਚ GST, ਡਿਊਟੀਆਂ ਅਤੇ ਸਥਾਨਕ ਟੈਕਸ ਸ਼ਾਮਲ (inclusive) ਹਨ ਜਾਂ ਬਾਹਰ (exclusive) ਹਨ।',
    'ਸਮਾਂ-ਸੀਮਾ ਦੀ ਤਸਦੀਕ (Timeline Verification): ਮੈਂ ਤਸਦੀਕ ਕਰਦਾ ਹਾਂ ਕਿ ਟੈਂਡਰ ਸ਼ੁਰੂ ਹੋਣ ਦੀ ਮਿਤੀ, ਬੋਲੀ ਜਮ੍ਹਾਂ ਕਰਨ ਦੀ ਆਖਰੀ ਮਿਤੀ, ਅਤੇ ਬੋਲੀ ਖੁੱਲ੍ਹਣ ਦੀ ਮਿਤੀ ਤਰਕਸੰਗਤ ਹਨ ਅਤੇ ਵੈਂਡਰਾਂ ਨੂੰ ਤਿਆਰੀ ਲਈ ਪੂਰਾ ਸਮਾਂ ਦਿੰਦੀਆਂ ਹਨ।',
    'ਵੈਂਡਰ ਦੀ ਯੋਗਤਾ (Vendor Eligibility): ਮੈਂ ਬੋਲੀ ਲਗਾਉਣ ਲਈ ਲੋੜੀਂਦੀਆਂ ਘੱਟੋ-ਘੱਟ ਤਕਨੀਕੀ ਅਤੇ ਵਿੱਤੀ ਯੋਗਤਾਵਾਂ (ਜਿਵੇਂ ਕਿ ਸਾਲਾਨਾ ਟਰਨਓਵਰ, ISO ਸਰਟੀਫਿਕੇਸ਼ਨ, ਜਾਂ ਪਿਛਲੇ ਪ੍ਰੋਜੈਕਟਾਂ ਦਾ ਤਜਰਬਾ) ਸਪੱਸ਼ਟ ਤੌਰ \'ਤੇ ਦੱਸੀਆਂ ਹਨ।',
    'EMD ਅਤੇ ਟੈਂਡਰ ਫੀਸ (EMD & Tender Fees): ਬਿਆਨਾ ਰਕਮ (Earnest Money Deposit - EMD), ਟੈਂਡਰ ਪ੍ਰੋਸੈਸਿੰਗ ਫੀਸ, ਅਤੇ ਭੁਗਤਾਨ ਦੇ ਸਵੀਕਾਰ ਕੀਤੇ ਜਾਣ ਵਾਲੇ ਤਰੀਕੇ ਸਪੱਸ਼ਟ ਤੌਰ \'ਤੇ ਦੱਸੇ ਗਏ ਹਨ।',
    'ਸਾਈਟ ਵਿਜ਼ਿਟ ਦੀਆਂ ਜ਼ਰੂਰਤਾਂ (Site Visit Requirements): ਜੇਕਰ ਬੋਲੀ ਲਗਾਉਣ ਤੋਂ ਪਹਿਲਾਂ ਸਾਈਟ ਦਾ ਮੁਆਇਨਾ ਕਰਨਾ ਜ਼ਰੂਰੀ ਹੈ, ਤਾਂ ਸੰਪਰਕ ਵਿਅਕਤੀ ਦੇ ਵੇਰਵੇ ਅਤੇ ਮੁਆਇਨੇ ਦਾ ਸਮਾਂ-ਸਾਰਣੀ ਦਿੱਤੀ ਗਈ ਹੈ।'
  ];

  // RESULT MODAL STATE
  const [resultPopup, setResultPopup] = useState(null);
  const [resultBids, setResultBids] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  const fetchTenders = async () => {
    setIsFetching(true);
    try {
      const q = query(collection(db, "tenders"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      setTenders(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error("Error fetching tenders:", err);
    }
    setIsFetching(false);
  };

  useEffect(() => { fetchTenders(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let fileUrl = existingFileUrl;
      if (tender.file) {
        const fileRef = ref(storage, `tenders/${Date.now()}_${tender.file.name}`);
        const snapshot = await uploadBytes(fileRef, tender.file);
        fileUrl = await getDownloadURL(snapshot.ref);
      }
      const data = { title: tender.title, description: tender.description, openDate: tender.openDate, closeDate: tender.closeDate, closeTime: tender.closeTime, fileUrl, createdAt: new Date(), status: "active" };
      if (editingId) { await updateDoc(doc(db, "tenders", editingId), data); setEditingId(null); } 
      else { await addDoc(collection(db, "tenders"), data); }
      setTender({ title: "", description: "", openDate: "", closeDate: "", file: null });
      fetchTenders();
      setChecklist([false, false, false, false, false, false, false]);
      alert("Success!");
    } catch (err) { alert(err.message); } 
    setLoading(false);
  };

  const handleDelete = async (id) => { if(window.confirm("Delete?")) { await deleteDoc(doc(db, "tenders", id)); fetchTenders(); } };
  const handleEdit = (t) => { setEditingId(t.id); setExistingFileUrl(t.fileUrl); setTender({ ...t, file: null }); window.scrollTo(0,0); };

  // --- OPEN RESULT POPUP & FETCH VENDOR DETAILS ---
  const viewResults = async (tender) => {
      setResultPopup(tender);
      setLoadingResults(true);
      setResultBids([]);
      try {
          const q = query(collection(db, "bids"), where("tenderId", "==", tender.id));
          const snap = await getDocs(q);
          
          // Fetch complete user details (GST/Aadhar) for each bid
          const fetchedBids = await Promise.all(snap.docs.map(async (d) => {
              const bidData = d.data();
              const userRef = doc(db, "users", bidData.userId);
              const userSnap = await getDoc(userRef);
              const userData = userSnap.exists() ? userSnap.data() : {};
              
              return {
                  ...bidData,
                  bidderGst: userData.gst || "Not Provided",
                  bidderPhone: userData.phone || bidData.bidderPhone || "Not Provided",
                  bidderAddress: userData.address || bidData.bidderAddress || "Not Provided"
              };
          }));

          fetchedBids.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
          setResultBids(fetchedBids);
      } catch (e) { console.error(e); }
      setLoadingResults(false);
  };

  // --- GENERATE PDF REPORT ---
  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank');
    
    // Build the table rows dynamically
    const tableRows = resultBids.map((bid, index) => `
      <tr style="background-color: ${index === 0 ? '#e8f5e9' : 'transparent'};">
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>${index === 0 ? '🏆 L1 (Winner)' : `L${index + 1}`}</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">${bid.bidderName}</td>
        <td style="padding: 12px; border: 1px solid #ddd;">${bid.bidderPhone}</td>
        <td style="padding: 12px; border: 1px solid #ddd;">${bid.bidderGst}</td>
        <td style="padding: 12px; border: 1px solid #ddd;">${bid.bidderAddress}</td>
        <td style="padding: 12px; border: 1px solid #ddd; font-weight: bold; color: #2c3e50;">Rs. ${Number(bid.amount).toLocaleString()}</td>
      </tr>
    `).join('');

    const htmlContent = `
      <html>
        <head>
          <title>Tender Results - ${resultPopup.title}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; }
            .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #2c3e50; padding-bottom: 20px; }
            .header h1 { margin: 0 0 10px 0; color: #2c3e50; font-size: 28px; }
            .header p { margin: 5px 0; color: #555; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 13px; }
            th { background-color: #2c3e50; color: white; padding: 15px 12px; text-align: left; border: 1px solid #2c3e50; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Official Tender Bidding Report</h1>
            <h2>Bathinda Housing Complex</h2>
            <p><strong>Tender Subject:</strong> ${resultPopup.title}</p>
            <p><strong>Reference ID:</strong> ${resultPopup.id.slice(0,8).toUpperCase()}</p>
            <p><strong>Report Generated On:</strong> ${new Date().toLocaleString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Bidder Name</th>
                <th>Phone Number</th>
                <th>GST / Aadhar No.</th>
                <th>Registered Address</th>
                <th>Bid Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <script>
            window.onload = function() { 
              setTimeout(() => { window.print(); window.close(); }, 500);
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <div className="manager-layout">
      <div className="form-card floating-card">
        <h3>{editingId ? "Edit Tender" : "Post New Tender"}</h3>
        <form onSubmit={handleSubmit}>
          <input className="input-field" placeholder="Title" value={tender.title} onChange={e => setTender({...tender, title: e.target.value})} required />
          <textarea className="input-field" placeholder="Description" rows="3" value={tender.description} onChange={e => setTender({...tender, description: e.target.value})} required />
          <div className="row-inputs">
   <label>Opens: <input type="date" className="input-field" value={tender.openDate} onChange={e => setTender({...tender, openDate: e.target.value})} required /></label>
   <label>Closes Date: <input type="date" className="input-field" value={tender.closeDate} onChange={e => setTender({...tender, closeDate: e.target.value})} required /></label>
   <label>Closes Time: <input type="time" className="input-field" value={tender.closeTime} onChange={e => setTender({...tender, closeTime: e.target.value})} required /></label>
</div>
          <div style={{marginBottom: '15px'}}>
            <input 
              type="file" 
              className="input-field" 
              accept="application/pdf"
              style={{marginBottom: '4px'}}
              onChange={e => {
                const selectedFile = e.target.files[0];
                if (selectedFile && selectedFile.size > 15 * 1024 * 1024) {
                  alert("⚠️ File size exceeds the 15MB limit. Please upload a smaller document.");
                  e.target.value = ""; // Clears the file from the input
                  setTender({...tender, file: null});
                } else {
                  setTender({...tender, file: selectedFile});
                }
              }} 
            />
            <div style={{ marginTop: '15px', background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #ddd' }}>
              <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#c0392b', marginBottom: '10px', display: 'block' }}>
                ਲਾਜ਼ਮੀ ਚੈਕਲਿਸਟ (Mandatory Checklist): 
              </label>
              {checklistItems.map((itemText, index) => (
                <label key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '12px', color: '#555', marginBottom: '8px', cursor: 'pointer', lineHeight: '1.4' }}>
                  <input 
                    type="checkbox" 
                    style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0 }}
                    checked={checklist[index]} 
                    onChange={(e) => {
                      const newChecklist = [...checklist];
                      newChecklist[index] = e.target.checked;
                      setChecklist(newChecklist);
                    }} 
                  />
                  <span>{itemText}</span>
                </label>
              ))}
            </div>
          </div>
          <button 
            type="submit" 
            disabled={loading || !allChecked} 
            className="btn-primary-full"
            style={{ opacity: (!allChecked && !loading) ? 0.6 : 1, marginTop: '10px' }}
          >
            {loading ? "⏳ Please wait, securely uploading..." : editingId ? "Update Tender" : "Publish Tender"}
          </button>
          
          {!allChecked && !loading && (
            <p style={{ color: '#c0392b', fontSize: '12px', textAlign: 'center', marginTop: '10px', fontWeight: 'bold' }}>
                ਕਿਰਪਾ ਕਰਕੇ ਟੈਂਡਰ ਪ੍ਰਕਾਸ਼ਿਤ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਉੱਪਰ ਦਿੱਤੀ ਸਾਰੀ ਚੈਕਲਿਸਟ ਦੀ ਪੁਸ਼ਟੀ ਕਰੋ।
            </p>
          )}

          {editingId && <button type="button" onClick={() => {setEditingId(null); setTender({ title: "", description: "", openDate: "", closeDate: "", file: null }); setChecklist([false, false, false, false, false, false, false]);}} className="btn-text">Cancel Edit</button>}
        </form>
      </div>
      
      <div className="list-card floating-card">
        <h3>Active Tenders</h3>
        <div className="list-scroll">
          {isFetching ? (
            <div style={{padding: '30px', textAlign: 'center', color: '#7f8c8d'}}>⏳ Fetching active tenders...</div>
          ) : tenders.length === 0 ? (
            <div style={{padding: '30px', textAlign: 'center', color: '#7f8c8d'}}>No tenders have been published yet.</div>
          ) : (
            tenders.map(t => {
            const closed = isTenderClosed(t.closeDate, t.closeTime);
return (
    <div key={t.id} className="list-item">
      <div style={{flex: 1}}>
          <strong>{t.title}</strong>
          <br/><small>{t.openDate} to {t.closeDate} {t.closeTime ? `at ${t.closeTime}` : ''}</small>
                      {closed && (
                          <span style={{
                              marginLeft:'10px', 
                              background:'#ffe3e3', 
                              color:'#c0392b', 
                              padding:'2px 6px', 
                              borderRadius:'4px', 
                              fontSize:'10px', 
                              fontWeight:'bold'
                          }}>
                              CLOSED
                          </span>
                      )}
                  </div>
                  <div className="item-actions">
                    {/* SHOW RESULT BUTTON ONLY IF CLOSED */}
                    {closed && (
                        <button onClick={() => viewResults(t)} className="btn-small result-btn" style={{background:'#6c5ce7', color:'white'}}>
                            View Bids
                        </button>
                    )}
                    
                    {/* NEW: ONLY SHOW EDIT AND DELETE IF TENDER IS STILL OPEN */}
                    {!closed && (
                        <>
                            <button onClick={() => handleEdit(t)} className="btn-small edit">Edit</button>
                            <button onClick={() => handleDelete(t.id)} className="btn-small delete">Del</button>
                        </>
                    )}
                  </div>
                </div>
            )
          })
        )}
        </div>
      </div>

      {/* --- ADMIN RESULT MODAL --- */}
      {resultPopup && (
          <div className="modal-overlay">
              <div className="modal-box" style={{maxWidth:'900px'}}>
                  <div className="modal-header">
                      <h3>Bidding Results: {resultPopup.title}</h3>
                      <button onClick={() => setResultPopup(null)} className="btn-close">✖</button>
                  </div>
                  <div className="modal-body" style={{overflowX: 'auto'}}>
                      {loadingResults ? (
                          <p style={{padding:'20px', textAlign:'center'}}>Loading results...</p>
                      ) : resultBids.length === 0 ? (
                          <p style={{padding:'20px', textAlign:'center'}}>No bids received for this tender.</p>
                      ) : (
                          <table className="admin-result-table" style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                              <thead style={{background:'#f8f9fa'}}>
                                  <tr>
                                      <th style={{padding:'10px', textAlign:'left'}}>Rank</th>
                                      <th style={{padding:'10px', textAlign:'left'}}>Bidder Name</th>
                                      <th style={{padding:'10px', textAlign:'left'}}>Phone</th>
                                      <th style={{padding:'10px', textAlign:'left'}}>Address</th>
                                      <th style={{padding:'10px', textAlign:'left'}}>Bid Amount</th>
                                      <th style={{padding:'10px', textAlign:'left'}}>Document</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {resultBids.map((bid, i) => {
                                      let bg = 'transparent';
                                      if(i===0) bg = '#fff9db'; // Gold for L1
                                      else if(i===1) bg = '#f1f2f6'; // Grey for L2

                                      return (
                                          <tr key={i} style={{background: bg, borderBottom:'1px solid #eee'}}>
                                              <td style={{padding:'10px'}}><strong>{i+1}</strong></td>
                                              <td style={{padding:'10px'}}>{bid.bidderName}</td>
                                              <td style={{padding:'10px'}}>{bid.bidderPhone || 'N/A'}</td>
                                              <td style={{padding:'10px', maxWidth:'200px'}}>{bid.bidderAddress || 'N/A'}</td>
                                              <td style={{padding:'10px'}}><strong>₹ {Number(bid.amount).toLocaleString()}</strong></td>
                                              <td style={{padding:'10px'}}>
                                                  {bid.fileUrl ? (
                                                      <a href={bid.fileUrl} target="_blank" rel="noreferrer" style={{color:'#3498db', fontWeight:'bold'}}>View PDF</a>
                                                  ) : (
                                                      <span style={{color:'#999'}}>No File</span>
                                                  )}
                                              </td>
                                          </tr>
                                      )
                                  })}
                              </tbody>
                          </table>
                      )}
                  </div>
                  <div className="modal-footer" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <button 
                      onClick={handleDownloadPDF} 
                      disabled={loadingResults || resultBids.length === 0}
                      className="btn-primary-full" 
                      style={{width: 'auto', background: '#27ae60', padding: '10px 20px'}}
                    >
                      🖨️ Download PDF Report
                    </button>
                    <button 
                      onClick={() => setResultPopup(null)} 
                      className="btn-text" 
                      style={{width: 'auto', margin: 0}}
                    >
                      Close Window
                    </button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
}

// --- 3. COMMITTEE MANAGER ---
function CommitteeManager() {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
  const [newMember, setNewMember] = useState({ name: "", role: "", phone: "", photo: null });

  const fetchMembers = async () => {
    const q = query(collection(db, "committee"), orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);
    setMembers(querySnapshot.docs.map(doc => ({ id: doc.id, order: 0, ...doc.data() })));
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let photoUrl = existingPhotoUrl;
      if (newMember.photo) {
        const fileRef = ref(storage, `committee/${Date.now()}_${newMember.photo.name}`);
        const snapshot = await uploadBytes(fileRef, newMember.photo);
        photoUrl = await getDownloadURL(snapshot.ref);
      }
      const memberData = { name: newMember.name, role: newMember.role, phone: newMember.phone, photoUrl: photoUrl };

      if (editingId) {
        await updateDoc(doc(db, "committee", editingId), memberData);
        setEditingId(null);
        alert("Updated!");
      } else {
        memberData.order = members.length + 1;
        await addDoc(collection(db, "committee"), memberData);
        alert("Added!");
      }
      setNewMember({ name: "", role: "", phone: "", photo: null });
      setExistingPhotoUrl("");
      fetchMembers();
    } catch (error) { alert("Error: " + error.message); }
    setLoading(false);
  };

  const handleMove = async (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === members.length - 1)) return;
    const newOrderList = [...members];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newOrderList[index], newOrderList[targetIndex]] = [newOrderList[targetIndex], newOrderList[index]];
    setMembers(newOrderList);
    
    const batch = writeBatch(db);
    newOrderList.forEach((member, i) => {
      const ref = doc(db, "committee", member.id);
      batch.update(ref, { order: i + 1 });
    });
    await batch.commit();
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    setExistingPhotoUrl(member.photoUrl || "");
    setNewMember({ name: member.name, role: member.role, phone: member.phone, photo: null });
    window.scrollTo(0,0);
  };

  const handleDelete = async (id) => { if(window.confirm("Remove?")) { await deleteDoc(doc(db, "committee", id)); fetchMembers(); } };
  const handleCancel = () => { setEditingId(null); setNewMember({ name: "", role: "", phone: "", photo: null }); setExistingPhotoUrl(""); };

  return (
    <div className="manager-layout">
      <div className="form-card floating-card">
        <h3>{editingId ? "Edit Member" : "Add Committee Member"}</h3>
        <form onSubmit={handleSubmit}>
          <input className="input-field" placeholder="Name" value={newMember.name} onChange={e => setNewMember({...newMember, name: e.target.value})} required />
          <input className="input-field" placeholder="Role" value={newMember.role} onChange={e => setNewMember({...newMember, role: e.target.value})} required />
          <input className="input-field" placeholder="Phone" value={newMember.phone} onChange={e => setNewMember({...newMember, phone: e.target.value})} required />
          <input className="input-field" type="file" accept="image/*" onChange={e => setNewMember({...newMember, photo: e.target.files[0]})} />
          <button type="submit" disabled={loading} className="btn-primary-full">{loading ? "Saving..." : (editingId ? "Update" : "Add")}</button>
          {editingId && <button type="button" onClick={handleCancel} className="btn-text">Cancel</button>}
        </form>
      </div>

      <div className="list-card floating-card">
        <h3>Committee List</h3>
        <div className="list-scroll">
          {members.map((m, index) => (
            <div key={m.id} className="list-item">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                 <div style={{display:'flex', flexDirection:'column', gap:'2px', marginRight:'5px'}}>
                    <button type="button" onClick={() => handleMove(index, 'up')} style={{border:'none', background:'#eee', cursor:'pointer', fontSize:'10px'}}>▲</button>
                    <button type="button" onClick={() => handleMove(index, 'down')} style={{border:'none', background:'#eee', cursor:'pointer', fontSize:'10px'}}>▼</button>
                 </div>
                <div style={{width: '40px', height: '40px', borderRadius: '50%', background: '#eee', overflow: 'hidden'}}>
                  {m.photoUrl ? <img src={m.photoUrl} alt="p" style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <span>👤</span>}
                </div>
                <div><strong>{m.name}</strong><br/><small className="highlight-text">{m.role}</small></div>
              </div>
              <div className="item-actions"><button onClick={() => handleEdit(m)} className="btn-small edit">Edit</button><button onClick={() => handleDelete(m.id)} className="btn-small delete">Del</button></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// 4. SETTINGS MANAGER
// ==========================================
function SettingsManager({ currentUser }) {
  const [pass, setPass] = useState("");
  
  if (!currentUser) return <p>Loading User Data...</p>;

  const isGoogleUser = currentUser.providerData.some(
    (provider) => provider.providerId === "google.com"
  );

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updatePassword(currentUser, pass);
      alert("✅ Password updated successfully!");
      setPass("");
    } catch (err) {
      alert("❌ Error: " + err.message + "\n(Try logging out and logging in again before changing password)");
    }
  };

  return (
    <div style={{ maxWidth: "500px", margin: "0 auto" }}>
      <div className="form-card floating-card">
        <h3>Account Security</h3>
        
        {isGoogleUser ? (
          <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>🔒</div>
            <p><strong>You are logged in with Google.</strong></p>
            <p style={{ fontSize: "13px" }}>
              To change your password, please go to your Google Account settings. 
              We do not manage Google passwords for security reasons.
            </p>
          </div>
        ) : (
          <>
            <p style={{ marginBottom: "15px", fontSize: "13px", color: "#666" }}>
              Enter a new password for your <strong>{currentUser.email}</strong> account.
            </p>
            <form onSubmit={handleUpdate}>
              <input 
                className="input-field" 
                type="password" 
                placeholder="New Password (min 6 chars)" 
                value={pass} 
                onChange={(e) => setPass(e.target.value)} 
                required 
                minLength={6} 
              />
              <button type="submit" className="btn-primary-full">
                Update Password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;